class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.boundClose = this.close.bind(this);
    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.bindOverlay();
    this.setHeaderCartIconAccessibility();
  }

  bindOverlay() {
    const nextOverlay = this.querySelector('#CartDrawer-Overlay');
    if (!nextOverlay || nextOverlay === this.overlay) return;
    this.overlay?.removeEventListener('click', this.boundClose);
    nextOverlay.addEventListener('click', this.boundClose);
    this.overlay = nextOverlay;
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink)
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {this.classList.add('animate', 'active')});

    this.addEventListener('transitionend', () => {
      const containerToTrapFocusOn = this.classList.contains('is-empty') ? this.querySelector('.drawer__inner-empty') : document.getElementById('CartDrawer');
      const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
      trapFocus(containerToTrapFocusOn, focusElement);
    }, { once: true });

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if(cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    const drawerInner = this.querySelector('.drawer__inner');
    if (drawerInner?.classList.contains('is-empty')) drawerInner.classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section => {
      const sectionElement = section.selector ? document.querySelector(section.selector) : document.getElementById(section.id);
      const sectionHTML = parsedState.sections?.[section.id];
      const replacement = sectionHTML ? this.getSectionInnerHTML(sectionHTML, section.selector) : null;
      if (sectionElement && replacement !== null) sectionElement.innerHTML = replacement;
    }));

    setTimeout(() => {
      this.bindOverlay();
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector)?.innerHTML ?? null;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer'
      },
      {
        id: 'cart-icon-bubble'
      }
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  onCartUpdate() {
    this.refreshRevision = (this.refreshRevision || 0) + 1;
    const revision = this.refreshRevision;
    this.refreshController?.abort();
    this.refreshController = new AbortController();
    const { signal } = this.refreshController;

    fetch(`${routes.cart_url}?section_id=cart-drawer`, { signal })
      .then((response) => {
        if (!response.ok) throw new Error('Unable to refresh cart drawer');
        return response.text();
      })
      .then((responseText) => {
        if (signal.aborted || revision !== this.refreshRevision || !this.isConnected) return;
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        const sourceDrawer = html.querySelector('#CartDrawer');
        const cartDrawer = this.closest('cart-drawer');
        const targetDrawer = cartDrawer?.querySelector('#CartDrawer');
        if (!sourceDrawer || !cartDrawer || !targetDrawer) {
          throw new Error('Cart drawer section is missing');
        }

        targetDrawer.innerHTML = sourceDrawer.innerHTML;
        cartDrawer.classList.toggle(
          'is-empty',
          Boolean(sourceDrawer.querySelector('.drawer__inner-empty'))
        );
        cartDrawer.bindOverlay();
      })
      .catch((error) => {
        if (signal.aborted || error?.name === 'AbortError') return;
        console.error(error);
      });
  }

  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner'
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section'
      }
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
